import {Component, EventEmitter, OnInit, Output, ViewChild} from '@angular/core';
import {HeaderToggleMenuEnum, MmFaultsSplitService} from "../services/mm-faults-split.service";
import {BehaviorSubject, combineLatest, firstValueFrom} from "rxjs";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {distinctUntilChanged, switchMap} from "rxjs/operators";
import {
  FloatingActionButton,
  FloatingActionButtonService,
  FloatingActionItem,
  FloatingNewActionButtonComponent
} from "@mydocma/component-library/src/lib/floating-action-button";
import {FloatButtonsTypesEnum, MmFaultsFabButtonService} from "../services/mm-faults-fab-button.service";
import {MmBimService} from "../../../mm-bim/mm-bim.service";
import {DeviceService} from "@mydocma/core/src/lib/device";
import {ProjectActionPermissionEnum} from "@docma-mm-core/permission/project-action-permission.enum";
import {MmProjectPermissionsService} from "../../../mm-project/mm-project-permissions-service";
import {MmTranslateService} from "../../../mm-translate.service";
import {ConnectivityService} from "@mydocma/core";
import {MmBimDialogService} from "../../../mm-bim/mm-bim-view/bim-dialog/bim-dialog.service";
import {MydocmaPromptButtonTypeEnum, PromptDialogService} from "@mydocma/component-library/src/lib/prompt-dialog";
import {MmFaultsViewService} from "../services/mm-faults-view.service";
import {SelectionService} from "@mydocma/component-library/src/lib/selection";
import {ViewFault} from "@docma-mm-core/fault/fault";
import {MmPlansManagerService} from "../../mm-plans-view/services/mm-plans-manager.service";
import {MmPlanEditService} from "../../mm-plans-view/mm-plan-view/services/mm-plan-edit.service";
import {MmProjectLoaderService} from "../../../mm-project/mm-project-loader.service";
import {MmFilterService} from "../../../mm-filter/mm-filter.service";
import {BimModel} from "../../../bim-viewer/src/lib/models/bim.model";
import {AppContainerService, AppLeftPaneService, SideNavBarService} from "@mydocma/app-container";
import {MmViewService} from "../../../mm-view.service";
import {MmFilterModel} from "../../../mm-filter/mm-filter.model";
import {MmPlanMobileViewService} from '../../mm-plans-view/services/mm-plan-mobile-view.service';
import {MmPlanViewService} from '../../mm-plans-view/mm-plan-view/services/mm-plan-view.service';
import {ColDefsService} from '../../mm-faults-ag-grid/col-defs/col-defs.service';
import {RowDefsService} from '../../mm-faults-ag-grid/row-defs/row-defs.service';
import {ChipListOptions} from '@mydocma/component-library/src/lib/chips/chip-list/chip-list-options';
import { ChipItem, ChipListStartAddNewEnum, ChipListViewTypeEnum } from '@mydocma/component-library/src/lib/chips';
import { MmPlansFilterService } from '../../mm-plans-view/services/mm-plans-filter.service';

@UntilDestroy()
@Component({
  selector: 'mydocma-mm-faults-view-header',
  templateUrl: './mm-faults-view-header.component.html',
  styleUrls: ['./mm-faults-view-header.component.scss'],
  providers: []
})
export class MmFaultsViewHeaderComponent implements OnInit {
  public static mmCreateFromDescription = 'mm-create-from-desc';
  public static mmCreateFromFile = 'mm-create-from-file';
  public static mmCreateFromPlan = 'mm-create-from-plan';
  public static mmCreateFromBim = 'mm-create-from-bim';
  @ViewChild('ref', {static: false}) floatingButton: FloatingNewActionButtonComponent;
  public headerToggleMenuEnum = HeaderToggleMenuEnum;
  permissionNewFault$ = this.permissionService.hasProjectActionPermissions([ProjectActionPermissionEnum.NewFault]);
  fabButtonSubject = new BehaviorSubject<FloatingActionButton>(null);
  fabButton$ = this.fabButtonSubject.asObservable();
  permissionDeleteAttachment$ = this.permissionService.hasProjectActionPermissions([ProjectActionPermissionEnum.DeleteAttachment]);
  permissionAddAttachment$ = this.permissionService.hasProjectActionPermissions([ProjectActionPermissionEnum.AddAttachment]);
  filterState: MmFilterModel;
  offline = false;
  @Output() onRefreshFaultsClicked = new EventEmitter();
  showTabletMeatballMenu = false;
  showStroke = !this.deviceService.isMobil();

  currentStructureChipOptions: ChipListOptions = {
    hideRemoveAll: true,
    viewType: ChipListViewTypeEnum.singleLine,
    addNewOptions: {
      startAdding: ChipListStartAddNewEnum.none,
      allowDuplication: false,
    },
    onChipRemove: async () => {
      this.filterService.setStructure(this.filterState.project.Structure);
    }
  }
  private currentStructureSubject = new BehaviorSubject<ChipItem[]>([]);
  public currentStructure$ = this.currentStructureSubject.asObservable();

  constructor(readonly faultsSplitService: MmFaultsSplitService,
              public readonly permissionService: MmProjectPermissionsService,
              public mmBimService: MmBimService,
              public connectivityService: ConnectivityService,
              public readonly bimDialogService: MmBimDialogService,
              public fabButtonService: MmFaultsFabButtonService,
              private appLeftPaneService: AppLeftPaneService,
              private sideNavBarService: SideNavBarService,
              private mmViewService: MmViewService,
              private readonly projectLoaderService: MmProjectLoaderService,
              public readonly selectionService: SelectionService<ViewFault>,
              private mmTranslateService: MmTranslateService,
              private readonly planEditService: MmPlanEditService,
              public readonly viewService: MmFaultsViewService,
              private translateService: MmTranslateService,
              private readonly filterService: MmFilterService,
              private readonly dialogService: PromptDialogService,
              private readonly plansManagerService: MmPlansManagerService,
              private fabService: FloatingActionButtonService,
              private mmPlanViewService: MmPlanViewService,
              private appContainerService: AppContainerService,
              private mobilePlanViewService: MmPlanMobileViewService,
              public deviceService: DeviceService,
              public colDefsService: ColDefsService,
              public rowDefsService: RowDefsService,
              private readonly plansFilterService: MmPlansFilterService) {
  }

  ngOnInit(): void {

    this.fabService.onFabButtonItemClick
      .pipe(untilDestroyed(this))
      .subscribe(value => {
        this.onFabItemClick(value);
      });
    this.connectivityService.appIsInOfflineMode
      .pipe(
        untilDestroyed(this),
        switchMap(o => {
          this.onOfflineChanged(o);
          return this.filterService.state;
        }),
      ).subscribe((fs) => {
      this.filterState = fs;
    })

    combineLatest([
      this.permissionNewFault$,
      this.permissionAddAttachment$,
      this.mmBimService.defaultBimModel$,
      this.faultsSplitService.rightViewVisible$.pipe(untilDestroyed(this), distinctUntilChanged()),
      this.mobilePlanViewService.activated$,
      this.mmPlanViewService.isFaultTileVisibleSubject
    ])
      .pipe(
        untilDestroyed(this),
      )
      .subscribe(([permissionNewFault, permissionAddAttachment, bimModel, rightViewVisibility, mobilPlanActivated, isFaultTileVisible]) => {
        this.onPlanViewClose(rightViewVisibility)
        let fabButton: FloatingActionButton = null;
        if (permissionNewFault) {
          if (mobilPlanActivated) {
            fabButton = {
              floatingDisabled: false,
              id: FloatButtonsTypesEnum.planView,
              iconName: 'add',
              tooltip: 'MM.fault.createFaultFromPlan',
            };

            if (isFaultTileVisible) {
              fabButton.class = 'mydocma-mm-fab-planview-button';
            } else {
              fabButton.class = '';
            }
          } else {
            fabButton = this.getFabButton(permissionAddAttachment, bimModel);
          }
        }
        if (fabButton) {
          this.translateFabButton(fabButton);
          this.fabButtonService.registerButton(fabButton);
        } else {
          this.fabButtonService.unregisterButton()
        }
        this.fabButtonSubject.next(fabButton);

      });
    this.mmTranslateService.onLangChange.pipe(untilDestroyed(this)).subscribe(value => {
      this.translateFabButton(this.fabButtonSubject.value);
    });

    this.filterService.state
      .pipe(untilDestroyed(this))
      .subscribe(state => {
        if(state.structure) {
          const currentStructureChip = {
            id: 'currentStructure',
            text: `${state.structure?.Name1}${(state.structure?.Name2 && this.showStroke ? (' - ' + state.structure?.Name2): '')}`,
            // tooltip: (this.translateService.instant('MM.fault.projectStructure')) + ` ${this.filterState.structure.Name1} - ${this.filterState.structure.Name2}`,
            iconName: this.showStroke ? '_stacked-organizational-chart-highlighted-first-node' : null,
          };
          this.currentStructureSubject.next([currentStructureChip]);
        } else if(this.currentStructureSubject.value.length = 0) {
          this.currentStructureSubject.next([]);
        }
      });
  }

  public emitRefreshFaults() {
    this.onRefreshFaultsClicked.emit(true);
  }

  async toggleSelectionMode() {
    this.selectionService.toggleSelectionMode();

    const selectionMode = await firstValueFrom(this.selectionService.selectionMode);

    if (selectionMode) {
      const currentFault = await firstValueFrom(this.viewService.currentFault);
      if (currentFault) {
        this.selectionService.selectItem(currentFault, true);
      }
    } else {
      const selectedItems = await firstValueFrom(this.selectionService.selectedItems);
      if (selectedItems && selectedItems.length > 0) {
        this.selectionService.deselectRange(selectedItems, false);
      }
    }
  }

  // this method listens to button or sub item click, depends on item type fire additional methods
  async onFabItemClick(btn: FloatingActionItem) {
    await this.stopSelectionMode();

    switch (btn.id) {
      case MmFaultsViewHeaderComponent.mmCreateFromDescription:
        await this.viewService.createFaultFromDescription();
        break;
      case MmFaultsViewHeaderComponent.mmCreateFromFile:
        await this.viewService.createFaultFromAttachments();
        break;
      case MmFaultsViewHeaderComponent.mmCreateFromPlan:
        //
        if(this.appContainerService.isMobileView){
          await this.plansManagerService.setSelectedPlan(null);
        }
        await this.createFaultFromPlan();
        break
      case FloatButtonsTypesEnum.planView:
        await this.createFaultFromPlan();
        break;
      case MmFaultsViewHeaderComponent.mmCreateFromBim:
        this.bimDialogService.openBimDialog(true);
        break;
      default:
        await this.onFabButtonClick();
    }
  }

  async createFaultFromPlan() {

    if (this.faultsSplitService.toggledRightViews.includes('planView') && this.plansManagerService.selectedPlan == null && (this.plansFilterService.filteredPlans?.length ?? 0) === 0) {
      this.dialogService.showNotificationDialog({
        title: this.translateService.instant('MM.plan.error.pinNotPossible'),
        subTitle: this.translateService.instant('MM.plan.error.noPlansOnStructure'),
        content: this.plansManagerService.allPlans.length == 0 ? this.translateService.instant('MM.plan.error.noPlansInProject')
          : this.translateService.instant('MM.plan.error.newFaultNoPlans.changeStructure')
      });
      return;
    } else if (!this.faultsSplitService.toggledRightViews.includes('planView') && !this.deviceService.isMobil()) {
      const planList = this.plansFilterService.filteredPlans;
      if (planList?.length === 0) {
        if (this.plansManagerService.allPlans.length > 0) {
          this.dialogService.showNotificationDialog({
            title: this.translateService.instant('MM.plan.error.pinNotPossible'),
            subTitle: this.translateService.instant('MM.plan.error.noPlansOnStructure'),
            content: this.translateService.instant('MM.plan.error.newFaultNoPlans.changeStructure')
          });
          return;
        } else if (this.plansManagerService.allPlans.length === 0) {
          this.dialogService.showNotificationDialog({
            title: this.translateService.instant('MM.plan.error.pinNotPossible'),
            content: this.translateService.instant('MM.plan.error.noPlansInProject')
          });
          return;
        }

      } else if (planList?.length === 1) {
        await this.plansManagerService.setSelectedPlan(planList[0]);
      } else {
        await this.plansManagerService.setSelectedPlan(null);
      }
    } else if (!this.plansManagerService.checkFaultInPlanStructure(this.filterState?.structure?.Id, this.plansManagerService.selectedPlan?.data?.Structure?.Id)) {
      this.dialogService.showNotificationDialog({
        title: this.translateService.instant('MM.plan.error.pinNotPossible'),
        subTitle: this.translateService.instant('MM.plan.error.faultPlanStructureInvalid'),
        content: this.translateService.instant('MM.plan.error.selectAnotherPlan'),
      });
      return;
    }
    //close sidenav bar for when we create from plan in tablet
    //might not work correctly for android tablets, cos device service will identity them as mobile device
    if (this.deviceService.isTablet()) {
      this.sideNavBarService.closeBar()
    }

    await this.viewService.createFaultFromDescription(MmFaultsViewHeaderComponent.mmCreateFromPlan);
    await this.planEditService.addFaultOnPin();


    //this.faultsSplitService.switchToPlanView(sender);
  }

  onResize($event) {
    setTimeout(() => {
      this.showTabletMeatballMenu = !this.deviceService.isMobil() && $event.contentRect.width - (document?.getElementsByClassName('structure-chiplist')[0]?.clientWidth || 0) < 450;
      this.showStroke = $event.contentRect.width > 350 && !this.deviceService.isMobil();
    }, 50);
  }

  onSubMenuOpened() {
    document.getElementsByClassName('multiline-dropdown')[0]?.parentElement?.setAttribute('style', 'margin-left: 4px; margin-right: 4px;');
    return this.showTabletMeatballMenu
  }

  private async onFabButtonClick() {
    if (this.deviceService.isTablet() && this.faultsSplitService.rightViewVisible) {
      await this.createFaultFromPlan();
    }
  }

  private onPlanViewClose(rightViewVisibility: boolean) {
    if (!rightViewVisibility && this.deviceService.isTablet() && this.faultsSplitService.currentToggleValue === 'planView') {
      //set previous toggle view in button toggle group
      this.faultsSplitService.setRightViewVisble(false);
    }
  }

  //depends on device type returns floating action buttons objects
  private getFabButton( permissionAddAttachment: boolean, bimModel: BimModel) {
    const fabButton: FloatingActionButton = {
      floatingDisabled: false,
      id: FloatButtonsTypesEnum.faultsView,
      iconName: 'add',
      tooltip: 'MM.fault.createFault',
      items: []
    };
    // if plan is open on table we remove sub item
    if (this.deviceService.isTablet() && this.faultsSplitService.rightViewVisible) {
      return fabButton;
    }
    fabButton.items.push({
      id: MmFaultsViewHeaderComponent.mmCreateFromDescription,
      text: 'MM.fault.createFaultFromDescription',
      iconName: '_align-left',
    })

    if (permissionAddAttachment) {
      fabButton.items.push(
        {
          id: MmFaultsViewHeaderComponent.mmCreateFromFile,
          text: 'MM.fault.createFaultFromAttachment',
          iconName: !this.deviceService.isDesktop() ? 'photo_camera' : '_attach',
          iconOutlined: true
        })
    }
    fabButton.items.push({
      id: MmFaultsViewHeaderComponent.mmCreateFromPlan,
      text: 'MM.fault.createFaultFromPlan',
      iconName: '_plan-view'
    });
    if (bimModel && !this.deviceService.isMobil()) {
      fabButton.items.push({
        id: MmFaultsViewHeaderComponent.mmCreateFromBim,
        text: 'MM.fault.createFaultFromBIMmodel',
        iconName: '_bim-model'
      });
    }
    return fabButton;

  }


  private translateFabButton(fabButton: FloatingActionButton) {
    const translations = new Map([[MmFaultsViewHeaderComponent.mmCreateFromDescription, 'MM.fault.createFaultFromDescription'],
      [MmFaultsViewHeaderComponent.mmCreateFromFile, 'MM.fault.createFaultFromAttachment'],
      [MmFaultsViewHeaderComponent.mmCreateFromPlan, 'MM.fault.createFaultFromPlan'],
      [MmFaultsViewHeaderComponent.mmCreateFromBim, 'MM.fault.createFaultFromBIMmodel'],
    ]);

    fabButton?.items?.forEach(value => {
      const key = translations.get(value.id);
      value.text = this.mmTranslateService.instant(key);
    });
  }

  private async stopSelectionMode() {
    if (!!(await firstValueFrom(this.selectionService.selectionMode))) {
      this.selectionService.stopSelectionMode();
    }
  }

  private onOfflineChanged(offline) {
    this.offline = offline;
    if (this.offline) {
      this.faultsSplitService.toggledLeftViews = ['tilesView'];
    }
  }

  setNewRowLineAmount(amount: number) {
    this.rowDefsService.currentLineAmount = amount;
  }

  switchToGridView(event) {
    this.faultsSplitService.toggleView(HeaderToggleMenuEnum.gridView);
    event.source?._elementRef?.nativeElement?.classList?.add('checked-fix');
  }

  switchToTileView(event) {
    this.faultsSplitService.toggleView(HeaderToggleMenuEnum.tileView)
    document.querySelector('.header-item .grid-button')?.classList?.remove('checked-fix');
  }

}
